"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Clock, Trophy, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const defaultMascot = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/af0573d1-cac5-46e2-816e-4e00b0b25dc3-1.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/af0573d1-cac5-46e2-816e-4e00b0b25dc3-1.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/af0573d1-cac5-46e2-816e-4e00b0b25dc3-1.png-wvc-sizes",
  alt: ""
};
const content = {
  specRail: {
    vi: "Phương pháp huấn luyện thi đấu",
    en: "Tournament Methodology"
  },
  altText: {
    vi: "Huấn luyện viên cùng 4 học sinh Robotics phân tích chiến thuật trên sa bàn thi đấu tiêu chuẩn",
    en: "Faculty coach strategizing with four robotics students around a regulation arena game mat"
  },
  coachRatio: {
    vi: "Tỷ lệ chuẩn 1 Huấn luyện viên : 6 Học viên",
    en: "Regulation 1:6 Coach Ratio"
  },
  matchSimulation: {
    vi: "Mô phỏng trận đấu 2.5 phút",
    en: "2.5 Min Match Simulation"
  },
  metrics: [{
    value: "1:6",
    label: {
      vi: "Tỷ lệ Huấn luyện viên : Học viên",
      en: "Coach-to-Student Ratio"
    }
  }, {
    value: {
      vi: "4 Giai đoạn",
      en: "4 Stages"
    },
    label: {
      vi: "Đánh giá quy chuẩn",
      en: "Systematic Review"
    },
    isSecondary: true
  }, {
    value: {
      vi: "24+ Phút",
      en: "24+ Mins"
    },
    label: {
      vi: "Thực chiến trên sa bàn",
      en: "Live Field Drills"
    }
  }],
  regimenBadge: {
    vi: "Quy chuẩn huấn luyện",
    en: "Coaching Regimen"
  },
  tracksBadge: {
    vi: "Đấu trường Quốc gia & Quốc tế",
    en: "National & International Tracks"
  },
  heading: {
    vi: "Quy trình chuẩn bị thi đấu Robotics & đấu trường công nghệ",
    en: "How tournament preparation works"
  },
  subtitle: {
    vi: "Học sinh tiến bộ từ phân tích cẩm nang luật thi đấu đến hoàn thiện robot thực chiến qua bốn giai đoạn huấn luyện chuyên sâu.",
    en: "Students move from initial rulebook analysis to field-tested robot routines through four structured training stages."
  },
  description: {
    vi: "Tại ROBOTO, quá trình chuẩn bị cho các giải đấu được xây dựng theo chuẩn mực các đội tuyển Engineering đại học thay vì những hoạt động giải trí tự do. Dưới sự dẫn dắt tận tâm của đội ngũ kỹ sư và giảng viên giàu kinh nghiệm, học sinh được tiếp cận toàn diện mọi khía cạnh của đấu trường: phân tích tỉ mỉ cẩm nang luật thi đấu để tối ưu hóa quỹ điểm, liên tục cải tiến hệ thống cơ khí trên sa bàn, chuẩn đoán và tối ưu mã nguồn Coding theo thời gian thực dưới áp lực đồng hồ đếm ngược, và tự tin thuyết trình bảo vệ nhật ký Engineering trước ban giám khảo chuyên môn.",
    en: "At ROBOTO, competition training is modeled after collegiate engineering teams rather than open-ended play. Under the personal guidance of faculty engineers, students systematically tackle every dimension of tournament play: dissecting complex game manuals to identify maximum-scoring paths, running rapid mechanical iterations on custom gear trains, executing real-time software diagnostics under countdown clocks, and articulating their engineering logbooks before technical judges."
  },
  stages: [{
    id: "01",
    title: {
      vi: "Phân tích & giải mã luật thi đấu",
      en: "Game rule deconstruction"
    },
    detail: {
      vi: "Các đội phân tích cẩm nang giải đấu, bảng ma trận điểm số và các điều kiện phạt để hoạch định quỹ đạo di chuyển cùng giá trị nhiệm vụ tối ưu.",
      en: "Teams analyze tournament manuals, score matrices, and penalty scenarios to map optimal pathing and mission values."
    }
  }, {
    id: "02",
    title: {
      vi: "Thử nghiệm cơ khí & tối ưu cấu trúc",
      en: "Iterative mechanical testing"
    },
    detail: {
      vi: "Học sinh lắp ráp, kiểm tra tải trọng hệ truyền động khung gầm và hiệu chuẩn cơ cấu nạp qua nhiều lượt chạy thực nghiệm trên sa bàn.",
      en: "Students build, stress-test chassis gearings, and calibrate intake mechanisms across multiple physical field runs."
    }
  }, {
    id: "03",
    title: {
      vi: "Tối ưu hóa Coding & gỡ lỗi theo thời gian",
      en: "Rapid timed software debugging"
    },
    detail: {
      vi: "Đội ngũ điều khiển và lập trình viên tinh chỉnh thuật toán tự hành, dữ liệu cảm biến cùng kịch bản khắc phục sự cố tức thì dưới áp lực đồng hồ giải đấu.",
      en: "Drivers and programmers tune autonomous PID loops, sensor readings, and real-time failure recoveries under competition clocks."
    }
  }, {
    id: "04",
    title: {
      vi: "Bảo vệ nhật ký Engineering & thuyết trình kỹ thuật",
      en: "Technical presentation defense"
    },
    detail: {
      vi: "Các thành viên hoàn thiện hồ sơ kỹ thuật, lập luận các giải pháp thiết kế cơ điện tử và bảo vệ cấu trúc mã nguồn Coding trước ban giám khảo.",
      en: "Teammates document their engineering notebooks, justify design trade-offs, and defend code architecture to panel judges."
    }
  }],
  ctaPrimary: {
    vi: "Đăng ký buổi học thử",
    en: "Book a trial session"
  },
  ctaSecondary: {
    vi: "Xem các bảng đấu thi đấu",
    en: "View competition tracks"
  }
};
export default function CompetitionPrep() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = React.useState<"vi" | "en">("vi");
  React.useEffect(() => {
    const getStoredLang = (): "vi" | "en" => {
      try {
        const stored = localStorage.getItem("roboto_lang");
        if (stored === "en" || stored === "vi") {
          return stored;
        }
      } catch {
        // Fallback gracefully on storage access errors
      }
      return "vi";
    };
    setLang(getStoredLang());
    const handleCustomLangChange = (event: Event) => {
      const customEvent = event as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (customEvent.detail?.lang === "vi" || customEvent.detail?.lang === "en") {
        setLang(customEvent.detail.lang);
      }
    };
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "roboto_lang" && (event.newValue === "vi" || event.newValue === "en")) {
        setLang(event.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomLangChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomLangChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="how-competition-prep-works" data-nav="light" className="relative w-full bg-background text-foreground py-20 lg:py-24 border-b border-border overflow-hidden">
      {/* Decorative coordinate blueprint background motif */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(hsl(258_20%_88%/0.4)_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="relative mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-16">
        {/* Top Header Rail: Spec Rail Accent & Static Floating Mascot */}
        <div className="flex items-center justify-between gap-4 mb-8 sm:mb-10">
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 bg-secondary rounded-sm" />
            <div className="h-px w-16 bg-secondary" />
            <span className="font-mono text-xs tracking-wider uppercase text-secondary font-bold">
              {content.specRail[lang]}
            </span>
          </div>

          {/* Static floating decorative mascot visual */}
          <div aria-hidden="true" className="pointer-events-none select-none shrink-0">
            <img src={defaultMascot.src} data-wvc-srcset={defaultMascot.srcset} data-wvc-sizes={defaultMascot.sizes} alt="" loading="lazy" decoding="async" className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 object-contain bg-transparent drop-shadow-md pointer-events-none select-none" />
          </div>
        </div>

        {/* Two-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Column 1: Photograph & Live Field Badge */}
          <motion.div className="lg:col-span-6 order-2 lg:order-1" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeIn}>
            {/* Tech frame outer container with double-corner brackets */}
            <div className="relative p-2">
              {/* Precision double-corner bracket frames in lime green */}
              <div aria-hidden="true" className="pointer-events-none absolute -top-1.5 -left-1.5 w-8 h-8 border-t-2 border-l-2 border-secondary z-20" />
              <div aria-hidden="true" className="pointer-events-none absolute -top-3 -left-3 w-5 h-5 border-t border-l border-secondary/50 z-20" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-1.5 -right-1.5 w-8 h-8 border-b-2 border-r-2 border-secondary z-20" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-3 -right-3 w-5 h-5 border-b border-r border-secondary/50 z-20" />

              {/* Sub-accent markers on remaining corners */}
              <div aria-hidden="true" className="pointer-events-none absolute -top-1.5 -right-1.5 w-4 h-4 border-t border-r border-secondary/40 z-20" />
              <div aria-hidden="true" className="pointer-events-none absolute -bottom-1.5 -left-1.5 w-4 h-4 border-b border-l border-secondary/40 z-20" />

              <div className="relative rounded-xl border border-border bg-card p-3 shadow-md">
                <div className="relative overflow-hidden rounded-lg aspect-[4/3] bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_competition_coaching_mat.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_competition_coaching_mat.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_competition_coaching_mat.png-wvc-sizes" alt={content.altText[lang]} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent pointer-events-none" />
                  
                  {/* Overlay Metadata Tag */}
                  <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4 flex flex-wrap items-center justify-between gap-2 pointer-events-none">
                    <div className="flex items-center gap-1.5 sm:gap-2 bg-primary/90 backdrop-blur-xs px-2.5 sm:px-3 py-1.5 rounded-md border border-white/20 text-primary-foreground text-xs font-mono font-medium shadow-xs">
                      <ShieldCheck className="w-3.5 h-3.5 text-secondary" />
                      <span>{content.coachRatio[lang]}</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-background/90 backdrop-blur-xs text-foreground px-2.5 sm:px-3 py-1.5 rounded-md border border-border/50 text-xs font-mono font-medium shadow-xs">
                      <Clock className="w-3.5 h-3.5 text-secondary" />
                      <span>{content.matchSimulation[lang]}</span>
                    </div>
                  </div>
                </div>

                {/* Lab Snapshot Metric Footer */}
                <div className="mt-3.5 pt-3 border-t border-border grid grid-cols-3 gap-2 text-center">
                  {content.metrics.map((metric, idx) => <div key={idx} data-index={idx} className="p-2 sm:p-2.5 rounded-lg bg-muted/60 border border-border/40">
                      <div className={`font-serif text-base sm:text-lg lg:text-xl font-bold tracking-tight ${metric.isSecondary ? "text-secondary" : "text-primary"}`}>
                        {typeof metric.value === "string" ? metric.value : metric.value[lang]}
                      </div>
                      <div className="text-[11px] sm:text-xs text-muted-foreground font-sans mt-0.5">
                        {metric.label[lang]}
                      </div>
                    </div>)}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Column 2: Content & Narrative Regimen */}
          <motion.div className="lg:col-span-6 order-1 lg:order-2 flex flex-col justify-center" initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeIn}>
            <div className="inline-flex flex-wrap items-center gap-2.5 mb-3.5">
              <span className="px-2.5 py-1 rounded-md text-xs font-mono font-bold tracking-wider uppercase bg-secondary/15 text-secondary border border-secondary/30">
                {content.regimenBadge[lang]}
              </span>
              <span className="text-xs text-muted-foreground font-mono uppercase tracking-wide flex items-center gap-1.5">
                <Trophy className="w-3.5 h-3.5 text-accent" />
                {content.tracksBadge[lang]}
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-foreground font-serif leading-[1.16] mb-4">
              {content.heading[lang]}
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-4 font-sans">
              {content.subtitle[lang]}
            </p>

            <p className="text-sm sm:text-base text-foreground/85 leading-relaxed font-sans mb-8">
              {content.description[lang]}
            </p>

            {/* Structured Stage Steps with Vertical Connecting Rail */}
            <div className="relative space-y-3 mb-8">
              {/* Secondary-colored vertical spec rail connecting the 4 stage boxes */}
              <div aria-hidden="true" className="pointer-events-none absolute left-[25px] top-4 bottom-4 w-0.5 bg-secondary/30 z-0" />

              {content.stages.map((stage, i) => <div key={stage.id} data-index={i} className="group relative overflow-hidden flex items-start gap-3.5 p-3.5 sm:p-4 rounded-xl border border-border bg-card shadow-xs transition-[border-color,box-shadow] hover:border-secondary/60 hover:shadow-md">
                  {/* Internal segment of connecting vertical rail */}
                  <div aria-hidden="true" className={`pointer-events-none absolute left-[25px] w-0.5 bg-secondary/30 z-0 ${i === 0 ? "top-[24px] bottom-0" : i === content.stages.length - 1 ? "top-0 h-6" : "top-0 bottom-0"}`} />

                  {/* Large semi-transparent stage number behind stage-box */}
                  <span aria-hidden="true" className="pointer-events-none absolute right-3 -bottom-3 select-none font-serif text-5xl sm:text-6xl font-bold tracking-tighter text-foreground/[0.05] group-hover:text-secondary/15 transition-colors">
                    {stage.id}
                  </span>

                  <div className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary/10 text-secondary font-mono text-xs font-bold mt-0.5 border border-secondary/40 shadow-xs">
                    {stage.id}
                  </div>
                  <div className="relative z-10 pr-10 sm:pr-14">
                    <h3 className="text-sm sm:text-base font-semibold text-foreground font-serif tracking-tight">
                      {stage.title[lang]}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mt-1 font-sans">
                      {stage.detail[lang]}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-3.5 sm:gap-4">
              <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-serif font-semibold px-7 py-3.5 rounded-lg text-sm transition-[background-color,box-shadow]">
                <Link to="/FreeTrialContactPage">
                  {content.ctaPrimary[lang]}
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-1.5 border-primary text-primary hover:bg-primary hover:text-primary-foreground shadow-xs font-serif font-semibold rounded-lg px-6 py-3.5 text-sm transition-colors">
                <Link to="/ProgramsPage" className="inline-flex items-center gap-2">
                  <span>{content.ctaSecondary[lang]}</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}